/**
 * Build the legacy classification route consumed by ListProductPage.
 *
 * ListProductPage splits the last dash as the NetSuite id and replaces `%`
 * characters with spaces for display, so every navigation surface must use the
 * same encoding until that route contract is migrated.
 *
 * **The `%` must be percent-encoded, not emitted raw.** A bare `%` is only legal in a URL
 * when followed by two hex digits, so `"Cements & Liners"` produced
 * `/products/by-class/cements%&%liners-5` and the dev server's Express threw
 * `URIError: Failed to decode param` before React ever mounted. Measured against the live
 * classification list: 92 of 178 names contain a space, **81 of them crashed** the route
 * and another 11 silently decoded to mojibake because their two following characters
 * happened to be valid hex (`endo%accessories` → `%ac` → byte 0xAC).
 *
 * `encodeURIComponent` turns the separator into `%25`, and React Router hands `useParams`
 * the decoded value — so `ListProductPage` still receives `cements%&%liners` and its
 * `replace(/%/g, " ")` still yields "cements & liners". The contract is unchanged; only
 * the wire form is now legal. The id is appended afterwards so the `-` separator survives
 * encoding.
 */
export function buildClassificationPath({ name = "", id } = {}) {
  const slug = encodeURIComponent(
    String(name).trim().replace(/\s+/g, "%").toLowerCase()
  );
  const hasId = id != null && String(id).trim() !== "";

  return `/products/by-class/${slug}${hasId ? `-${id}` : ""}`;
}

/**
 * Build the `/products/by-brand/:brandName` route.
 *
 * **Pass the brand name exactly as NetSuite returns it — a slug will not match.**
 * `ListProductPage` forwards the param straight to `GET_ITEMS_BY_BRAND`, and the service
 * compares it with `LOWER(i.custitemcustitem_dnd_brand) = LOWER(:value)`
 * (`item.service.js` → `findByField`). Case is therefore free, but nothing else is:
 * measured against the live data, `medicom` returns products while
 * `johnson-and-johnson` returns **0** — no dash-for-space or `and`-for-`&` normalisation
 * happens anywhere in the chain. `Johnson & Johnson` is the value that works.
 *
 * `encodeURIComponent` is what makes the awkward names routable: 6 of the 313 brands
 * contain `&` or `/` (`Johnson & Johnson`, `Coltene/Whaledent`, `L&R`, `P&G`,
 * `Pelton & Crane`, `W&H`). React Router hands `useParams` the decoded value, so the
 * page still receives the exact name.
 */
export function buildBrandPath(name) {
  return `/products/by-brand/${encodeURIComponent(String(name).trim())}`;
}
